import { Clock, LucideIcon, ShieldCheck } from "lucide-react";
import { PostabilityReason } from "@/services/bedrock/dtos/Review";

export interface PostabilityDisplay {
  reason: PostabilityReason;
  icon: LucideIcon;
  title: string;
  message: string;
}

// When several apply, the one that would still block after the others were fixed comes first.
const PRIORITY: PostabilityReason[] = ["analysis_pending", "not_positive", "inactive", "test", "not_approved"];

/** The reason a review can't be posted that is worth telling the player, or null when it can be. */
export function primaryPostabilityReason(reasons: PostabilityReason[] | undefined): PostabilityReason | null {
  if (!reasons || reasons.length === 0) return null;
  return PRIORITY.find(reason => reasons.includes(reason)) ?? reasons[0];
}

function firstName(reviewerName: string | null | undefined): string | null {
  const first = reviewerName?.trim().split(/\s+/)[0];
  return first ? first : null;
}

export function postabilityDisplay(reason: PostabilityReason, reviewerName?: string | null): PostabilityDisplay {
  switch (reason) {
    case "analysis_pending":
      return {
        reason,
        icon: Clock,
        title: "Almost Ready to Post",
        message: "We're still processing this review. Check back in a minute.",
      };
    case "not_positive":
      return {
        reason,
        icon: ShieldCheck,
        title: "Post This Review",
        message: "Only positive reviews can be posted, so this one stays off your socials.",
      };
    case "inactive":
      return {
        reason,
        icon: ShieldCheck,
        title: "Post This Review",
        message: "This review is no longer active, so it can't be posted.",
      };
    case "test":
      return {
        reason,
        icon: ShieldCheck,
        title: "Post This Review",
        message: "This is a test review, so it can't be posted.",
      };
    case "not_approved":
      return {
        reason,
        icon: ShieldCheck,
        title: "Post This Review",
        message: `${firstName(reviewerName) ?? "The reviewer"} didn't give permission to share this review.`,
      };
  }
}
